import React from 'react';
import { Formik } from 'formik';
import {
    Modal,
    TextField,
    Select,
    MenuItem,
    Button,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const CollectOrderState = {
    InProgress: "InProgress",
    ReadyForPickup: "ReadyForPickup",
    PickedUp: "PickedUp",
    Canceled: "Canceled",
};

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        '& .MuiInputBase-root': {
            margin: theme.spacing(1),
            width: 320,
        },
        '& .MuiSelect-select': {
            padding: "10.5px 14px",
        },
        '& label': {
            marginLeft: theme.spacing(1),
            marginBottom: '0',
            fontSize: '0.8rem'
        },
        '& .MuiButtonBase-root': {
            margin: theme.spacing(1),
            width: 320,
            padding: "9px 14px",
            marginTop: "12px",
        },
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1),
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    paper: {
        position: 'absolute' as const,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: "5px",
        borderRadius: "4px",
        outline: "none"
    },
    paperInner: {
        display: "flex",
        alignItems: "center",
        '&>*': {
            margin: '7px'
        }
    }
}));

interface OrderFormProps {
    mode: string;
    initialValues: Record<string, any>;
    stores: { Id: any; Title: string }[];
    onClose: () => void;
    onSubmit: (values: any) => void;
    onReadyAndSend: (values: any) => void;
}

export default function OrderForm({ mode, initialValues, stores, onClose, onSubmit, onReadyAndSend }: OrderFormProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <Modal open={true} className={classes.root}>
            <div tabIndex={-1} className={classes.paper}>
                <div className={classes.paperInner}>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={(values, { setSubmitting }) => {
                            onSubmit(values);
                            setSubmitting(false);
                        }}
                    >
                        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="flex-col">
                                    <h3>
                                        {mode === 'Add' ? t("dform.add") : t("dform.edit")} {t("dform.item")}
                                    </h3>
                                    <div className={classes.formGrid}>
                                        <div className="form-group flex-col">
                                            <label htmlFor="Title">{t('orderstable.title')}</label>
                                            <TextField
                                                id="Title"
                                                name="Title"
                                                required
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.Title}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </div>

                                        <div className="form-group flex-col">
                                            <label htmlFor="OrderNumber">{t('orderstable.order-number')}</label>
                                            <TextField
                                                id="OrderNumber"
                                                name="OrderNumber"
                                                required
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.OrderNumber}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </div>

                                        <div className="form-group flex-col">
                                            <label htmlFor="StoreId">{t('orderstable.store')}</label>
                                            <Select
                                                id="StoreId"
                                                name="StoreId"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                defaultValue={values.StoreId}
                                                variant="outlined"
                                            >
                                                {stores.map(store => (
                                                    <MenuItem key={store.Id} value={store.Id}>
                                                        {store.Title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </div>

                                        <div className="form-group flex-col">
                                            <label htmlFor="Email">{t('orderstable.email')}</label>
                                            <TextField
                                                id="Email"
                                                name="Email"
                                                type="email"
                                                required
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.Email}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </div>

                                        <div className={`form-group flex-col ${classes.fullWidth}`}>
                                            <label htmlFor="Freetext">{t('orderstable.freetext')}</label>
                                            <TextField
                                                id="Freetext"
                                                name="Freetext"
                                                multiline
                                                minRows={4}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.Freetext}
                                                variant="outlined"
                                                size="small"
                                            />
                                        </div>

                                        <div className={`form-group flex-col ${classes.fullWidth}`}>
                                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                                <div className="form-group flex-col">
                                                    <label htmlFor="State">{t('orderstable.status')}</label>
                                                    <Select
                                                        id="State"
                                                        name="State"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        defaultValue={values.State}
                                                        variant="outlined"
                                                    >
                                                        <MenuItem value={CollectOrderState.InProgress}>
                                                            {t('orderstable.state.in-progress')}
                                                        </MenuItem>
                                                        <MenuItem value={CollectOrderState.ReadyForPickup} disabled>
                                                            {t('orderstable.state.ready-for-pickup')}
                                                        </MenuItem>
                                                        <MenuItem value={CollectOrderState.PickedUp}>
                                                            {t('orderstable.state.picked-up')}
                                                        </MenuItem>
                                                        <MenuItem value={CollectOrderState.Canceled}>
                                                            {t('orderstable.state.canceled')}
                                                        </MenuItem>
                                                    </Select>
                                                </div>
                                                {mode === 'Edit' && values.HasLockers && (
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        disabled={isSubmitting}
                                                        onClick={() => onReadyAndSend(values)}
                                                        style={{ width: 'auto' }}
                                                    >
                                                        {t("orderstable.ready-and-send")}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex">
                                        <Button variant="contained" onClick={onClose}>
                                            {t('generic.cancel')}
                                        </Button>
                                        <Button
                                            id="formSubmitBtn"
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={isSubmitting}
                                        >
                                            {t("generic.save")}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>
        </Modal>
    );
}
